import * as firebase from 'firebase/app'
import 'firebase/auth';

import * as Sentry from '@sentry/browser'

import config from '@/config'
import store from '@/store'

import graphQLClient from '@/graphql/client'

import authMeQuery from '@/graphql/queries/authMe'

import sendVerificationEmailMutation from '@/graphql/mutations/sendVerificationEmail'
import sendPasswordResetEmailMutation from '@/graphql/mutations/sendPasswordResetEmail'
import sendWelcomeParentEmailMutation from '@/graphql/mutations/sendWelcomeParentEmail'
import updateUserMutation from '@/graphql/mutations/updateUser'
import updateUserLastLoginMutation from '@/graphql/mutations/updateUserLastLogin'

let userAuthPromiseResolve;
export let userAuthPromise = new Promise(resolve => userAuthPromiseResolve = resolve);

export const handleUserAuthChange = async user => {
  if (user) {
    Sentry.configureScope(scope => {
      const { uid: id, email } = user
      scope.setUser({ id, email })
    })

    if (IS_CREATING_USER) return

    const { authMe } = (await graphQLClient.query({
      query: authMeQuery
    })).data

    const { id: userId, fullName } = authMe
    const userHasIdDocument = !!authMe.identDoc

    store.commit('setCurrentUser', {
      id: userId,
      fullName,
      ...user,
    })
    store.commit('setUserHasIdDocument', userHasIdDocument)
    updateLastLogin()
  } else {
    Sentry.configureScope(scope => {
      scope.setUser({ id: null, email: null })
    })
    store.commit('setCurrentUser', null)
  }

  userAuthPromiseResolve()

  if (LOGIN_AUTH_CHANGED_PROMISE_RESOLVE) {
    LOGIN_AUTH_CHANGED_PROMISE_RESOLVE()
    LOGIN_AUTH_CHANGED_PROMISE_RESOLVE = null
    LOGIN_AUTH_CHANGED_PROMISE = null
  }
}

let IS_CREATING_USER = false
let LOGIN_AUTH_CHANGED_PROMISE = null
let LOGIN_AUTH_CHANGED_PROMISE_RESOLVE = null

firebase.initializeApp(config.firebaseConfig)
firebase.auth().onAuthStateChanged(handleUserAuthChange)

export const signup = async (model, slug, subpropertySiteName) => {
  const { email, password } = model

  const site = process.env.VUE_APP_SITE
  
  IS_CREATING_USER = true

  const user = await firebase.auth().createUserWithEmailAndPassword(email, password)

  const profile = Object.assign({
    preferredLanguage: localStorage.getItem('language'),
    originSubproperty: store.getters['subpropertyId'],
  }, model)
  
  delete profile.id
  delete profile.password
  delete profile.confirmPassword
  delete profile.tosAccepted

  const updatedUser = await graphQLClient.mutate({
    mutation: updateUserMutation,
    variables: { profile },
  })

  IS_CREATING_USER = false
  handleUserAuthChange(user)

  graphQLClient.mutate({
    mutation: sendVerificationEmailMutation,
    variables: { site, slug },
  })

  graphQLClient.mutate({
    mutation: sendWelcomeParentEmailMutation,
    variables: { slug, subpropertySiteName },
  })

  return updatedUser
}

export const forgotPassword = async (email, originUrl) => {

  return await graphQLClient.mutate({
    mutation: sendPasswordResetEmailMutation,
    variables: { email, originUrl },
  })
}

export const login = async ({ email, password, rememberMe = false}) => {
  const persistence = rememberMe
    ? firebase.auth.Auth.Persistence.LOCAL
    : firebase.auth.Auth.Persistence.SESSION

  LOGIN_AUTH_CHANGED_PROMISE = new Promise(resolve => LOGIN_AUTH_CHANGED_PROMISE_RESOLVE = resolve)

  await firebase.auth().setPersistence(persistence)
  const login = await firebase.auth().signInWithEmailAndPassword(email, password)
  await LOGIN_AUTH_CHANGED_PROMISE

  return login
}

export const logout = () => firebase.auth().signOut()

export const isLoggedIn = async () => !!(await firebase.auth().currentUser)

export const updateLastLogin = () => graphQLClient.mutate({ mutation: updateUserLastLoginMutation })
